// Phase 39 — click-to-call affordance for collaborator rosters. Wraps the
// shared InitialsBadge in a button that rings a colleague (or, when a call is
// already live, adds them to it). A small phone glyph appears on hover so the
// affordance is discoverable without a persistent badge; the click stops
// propagation so it never collides with a row/profile click underneath. Falls
// back to a plain badge for "me" or when no handler is wired.

import { useState } from "react";
import { Phone } from "lucide-react";
import { useTranslation } from "../i18n";
import { pname } from "../utils";
import { InitialsBadge } from "./SharedUI";
import type { Collaborator } from "@shared/entities";
import { CT, RAD } from "../lib/tokens";

type CallAvatarProps = {
  person?: Collaborator;
  isMe?: boolean;
  onCall?: (id: any) => void;
  callActive?: boolean;
  disabled?: boolean;
  [key: string]: any;
};

export default function CallAvatar({ person, isMe, onCall, callActive = false, disabled = false, ...rest }: CallAvatarProps) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);

  if (isMe || !onCall || !person || person.id == null) {
    return <InitialsBadge person={person} isMe={isMe} {...(rest as any)} />;
  }

  const name = pname(person);
  const title = disabled
    ? t("calls.window.full", { fallback: "Call is full (max 4)" })
    : callActive
      ? t("calls.add_colleague", { name, fallback: `Add ${name} to the call` })
      : t("calls.call_colleague", { name, fallback: `Call ${name}` });

  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onCall(person.id);
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        padding: 0,
        margin: 0,
        border: "none",
        background: "none",
        cursor: disabled ? "default" : "pointer",
        lineHeight: 0,
        borderRadius: RAD.pill,
        display: "inline-flex",
      }}
    >
      <InitialsBadge person={person} isMe={isMe} {...(rest as any)} />
      {hover && !disabled && (
        <span
          style={{
            position: "absolute",
            right: -3,
            bottom: -3,
            background: callActive ? "var(--ac)" : "var(--gn)",
            color: CT.c_fff,
            borderRadius: RAD.pill,
            width: 13,
            height: 13,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 0 1.5px var(--bc)",
          }}
        >
          <Phone size={8} />
        </span>
      )}
    </button>
  );
}
